import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import AppErrorBoundary from './components/AppErrorBoundary';
import GlobalRuntimeErrorHandler from './components/GlobalRuntimeErrorHandler';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { useAuth } from './context/AuthContext';
import { getAppEntryPath } from './lib/authRouting';

// Pages
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PublicShowcasePage from './pages/PublicShowcasePage';
import PhotoWallCapturePage from './pages/public/PhotoWallCapturePage';
import PhotoWallProjectorPage from './pages/PhotoWallProjectorPage';
import AuthPage from './pages/auth/AuthPage';
import InvitePage from './pages/auth/InvitePage';
import LandingPageV3 from './pages/LandingPageV3';

// Event Manager Dashboard
import EventManagerDashboard from './pages/dashboard/EventManagerDashboard';
import EventHome from './pages/dashboard/EventHome';
import CreateEvent from './pages/dashboard/CreateEvent';
import EventThemeSettings from './pages/dashboard/EventThemeSettings';
import GuestManagement from './pages/dashboard/GuestManagement';
import RoomManagement from './pages/dashboard/RoomManagement';
import TaskBoard from './pages/dashboard/TaskBoard';
import GiftRegistry from './pages/dashboard/GiftRegistry';
import VendorManagement from './pages/dashboard/VendorManagementShared';
import ItineraryManagement from './pages/dashboard/ItineraryManagement';
import KeyContacts from './pages/dashboard/KeyContacts';
import PhotoManagement from './pages/dashboard/PhotoManagement';
import ShowcaseManagement from './pages/dashboard/ShowcaseManagement';
import Settings from './pages/dashboard/Settings';
import WhatsAppLogs from './pages/dashboard/WhatsAppLogs';
import AppLogs from './pages/dashboard/AppLogs';
import BudgetManagement from './pages/dashboard/BudgetManagement';
import CelebrationActivityStudio from './pages/dashboard/BabyShowerGameStudio';
import SocialContentStudio from './pages/social/SocialContentStudio';

// Guest Dashboard
import GuestDashboard from './pages/guest/GuestDashboard';
import MyTickets from './pages/guest/MyTickets';
import MyRoom from './pages/guest/MyRoom';
import RSVPPage from './pages/guest/RSVPPage';
import WardrobePlanner from './pages/guest/WardrobePlanner';

// Couple Dashboard
import CoupleDashboard from './pages/couple/CoupleDashboard';

import './App.css';

function AppEntryRedirect() {
  const { user, loading, effectiveRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wedding-cream">
        <div className="w-16 h-16 animate-spin rounded-full border-4 border-wedding-sage border-t-transparent" />
      </div>
    );
  }

  return <Navigate to={getAppEntryPath({ user, effectiveRole })} replace />;
}

function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <GlobalRuntimeErrorHandler />
          <BrowserRouter>
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPageV3 />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/showcase/:slug" element={<PublicShowcasePage />} />
            <Route path="/showcase/:slug/:eventId" element={<PublicShowcasePage />} />
            <Route path="/share/:token" element={<PhotoWallCapturePage />} />
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <AuthPage />
                </PublicRoute>
              } 
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <AuthPage />
                </PublicRoute>
              }
            />
            <Route path="/invite" element={<InvitePage />} />

            {/* Event Manager Routes */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <EventHome />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute allowedRoles={['event_manager']}>
                  <EventManagerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard/events/new" 
              element={
                <ProtectedRoute allowedRoles={['event_manager', 'guest', 'admin_guest', 'bride', 'groom', 'host']}>
                  <CreateEvent />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard/theme" 
              element={
                <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host']} requiredEventFeature="showInviteStudio">
                  <EventThemeSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard/guests" 
              element={
                <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host', 'admin_guest']}>
                  <GuestManagement />
                </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/rooms" 
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host', 'admin_guest', 'guest', 'hotel_manager']} requireAdminAccess requiredEventFeature="showAccommodation">
                <RoomManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/itinerary" 
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host']} requiredEventFeature="showItinerary">
                <ItineraryManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
              path="/dashboard/tasks" 
              element={
              <ProtectedRoute allowedRoles={['event_manager', 'team_member', 'bride', 'groom', 'host']}>
                <TaskBoard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/gifts" 
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host']} requiredEventFeature="showWishlist">
                <GiftRegistry />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/vendors" 
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host']}>
                <VendorManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/contacts" 
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host']}>
                <KeyContacts />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard/photos" 
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host']}>
                <PhotoManagement />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/dashboard/social"
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host']} requiredEventFeature="showSocialStudio">
                <SocialContentStudio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/budget"
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'team_member']}>
                <BudgetManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/photo-wall/projector"
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host']}>
                <PhotoWallProjectorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/showcase"
            element={
              <ProtectedRoute allowedRoles={['event_manager']}>
                <ShowcaseManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/showcase"
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'manager']} accountRoleOnly>
                <ShowcaseManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'team_member', 'bride', 'groom', 'host', 'guest', 'admin_guest']}>
                <Settings />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/account/settings"
            element={
              <ProtectedRoute accountRoleOnly>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/whatsapp-logs"
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host', 'admin_guest']}>
                <WhatsAppLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dev/logs"
            element={<AppLogs />}
          />

          {/* Guest Routes */}
          <Route 
            path="/guest" 
            element={
              <ProtectedRoute allowedRoles={['guest', 'admin_guest', 'bride', 'groom', 'host']}>
                <GuestDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/guest/tickets" 
            element={
              <ProtectedRoute allowedRoles={['guest', 'admin_guest', 'bride', 'groom', 'host']} requiredEventFeature="showTravelAndId">
                <MyTickets />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/guest/room" 
            element={
              <ProtectedRoute allowedRoles={['guest', 'admin_guest', 'bride', 'groom', 'host']} requiredEventFeature="showAccommodation">
                <MyRoom />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/guest/itinerary" 
            element={
              <ProtectedRoute allowedRoles={['guest', 'admin_guest', 'bride', 'groom', 'host']} requiredEventFeature="showItinerary">
                <ItineraryManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/guest/wardrobe" 
            element={
              <ProtectedRoute allowedRoles={['guest', 'admin_guest', 'bride', 'groom', 'host']} requiredEventFeature="showWardrobe">
                <WardrobePlanner />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/guest/photos" 
            element={
              <ProtectedRoute allowedRoles={['guest', 'admin_guest', 'bride', 'groom', 'host']}>
                <PhotoManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/guest/gifts" 
            element={
              <ProtectedRoute allowedRoles={['guest', 'admin_guest', 'bride', 'groom', 'host']} requiredEventFeature="showWishlist">
                <GiftRegistry />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/guest/rsvp" 
            element={
              <ProtectedRoute allowedRoles={['guest', 'admin_guest', 'bride', 'groom', 'host']}>
                <RSVPPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/guest/contacts" 
            element={
              <ProtectedRoute allowedRoles={['guest', 'admin_guest', 'bride', 'groom', 'host']}>
                <KeyContacts />
              </ProtectedRoute>
            } 
          />

          {/* Couple Routes */}
          <Route 
            path="/couple" 
            element={
              <ProtectedRoute allowedRoles={['bride', 'groom', 'host']}>
                <CoupleDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/couple/guests" 
            element={
              <ProtectedRoute allowedRoles={['bride', 'groom', 'host']}>
                <GuestManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/couple/rooms" 
            element={
              <ProtectedRoute allowedRoles={['bride', 'groom', 'host']} requiredEventFeature="showAccommodation">
                <RoomManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/couple/photos" 
            element={
              <ProtectedRoute allowedRoles={['bride', 'groom', 'host']}>
                <PhotoManagement />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/couple/gifts"
            element={
              <ProtectedRoute allowedRoles={['bride', 'groom', 'host']} requiredEventFeature="showWishlist">
                <GiftRegistry />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/couple/vendors" 
            element={
              <ProtectedRoute allowedRoles={['bride', 'groom', 'host']}>
                <VendorManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/couple/tasks" 
            element={
              <ProtectedRoute allowedRoles={['bride', 'groom', 'host']}>
                <TaskBoard />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/dashboard/games"
            element={
              <ProtectedRoute allowedRoles={['event_manager', 'bride', 'groom', 'host']} requiredEventFeature="showCelebrationGames">
                <CelebrationActivityStudio />
              </ProtectedRoute>
            }
          />

          {/* Generic Host Routes */}
          <Route path="/host" element={<ProtectedRoute allowedRoles={['host']}><CoupleDashboard /></ProtectedRoute>} />
          <Route path="/host/guests" element={<ProtectedRoute allowedRoles={['host']}><GuestManagement /></ProtectedRoute>} />
          <Route path="/host/rooms" element={<ProtectedRoute allowedRoles={['host']} requiredEventFeature="showAccommodation"><RoomManagement /></ProtectedRoute>} />
          <Route path="/host/photos" element={<ProtectedRoute allowedRoles={['host']}><PhotoManagement /></ProtectedRoute>} />
          <Route path="/host/gifts" element={<ProtectedRoute allowedRoles={['host']} requiredEventFeature="showWishlist"><GiftRegistry /></ProtectedRoute>} />
          <Route path="/host/vendors" element={<ProtectedRoute allowedRoles={['host']}><VendorManagement /></ProtectedRoute>} />
          <Route path="/host/tasks" element={<ProtectedRoute allowedRoles={['host']}><TaskBoard /></ProtectedRoute>} />

          {/* Catch all */}
            <Route path="*" element={<AppEntryRedirect />} />
          </Routes>
          </BrowserRouter>
          <PWAInstallPrompt />
          <Toaster position="top-right" />
        </ThemeProvider>
      </AuthProvider>
    </AppErrorBoundary>
  );
}

export default App;
